import { Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import { Suspense, useEffect, useState } from 'react';
import { lazyRoute } from '@/utils/lazyRoute';
import { AuthCallback } from '@/components/auth/AuthCallback';
// Lazy: the modal is the only eager user of motion's animation runtime, and
// importing it here put ~120 KiB into the entry chunk for every visitor.
const WelcomeModal = lazyRoute(() => import('@/components/auth/WelcomeModal'));
import { toast } from 'sonner';
import { supabase, supabaseConfig } from '@/integrations/supabase/client';
import { useThemeEffect } from '@/hooks/useThemeEffect';
import { useDedupeStaticMeta } from '@/hooks/useDedupeStaticMeta';
import { AdminProtection } from '@/components/admin/AdminProtection';
import { ChatPopupProvider, useChatPopup } from '@/contexts/ChatPopupContext';
import { FeedsProvider } from '@/contexts/FeedsProvider';
import LegacyRouteRedirect from '@/routing/LegacyRouteRedirect';
import { beginAppReadiness, probeDatabase } from '@/startup/appReadiness';
import AccountLayout from '@/components/layout/AccountLayout';
import AdminLayout from '@/components/layout/AdminLayout';
import FocusedLayout from '@/components/layout/FocusedLayout';
import PublicLayout from '@/components/layout/PublicLayout';
import RouteAnnouncer from '@/components/layout/RouteAnnouncer';
import ScrollRestoration from '@/components/layout/ScrollRestoration';
import CrossHostRedirect from '@/routing/CrossHostRedirect';
import { isStudioHost } from '@/routing/studioHost';
import StudioRoutes from '@/app/routes/StudioRoutes';
import SequenceUsageResume from '@/components/sequences/SequenceUsageResume';
import { AnalyticsIdentity } from '@/components/analytics/AnalyticsIdentity';
import SidewalkUpvoteMerger from '@/components/SidewalkUpvoteMerger';
import IntentPrompt from '@/components/onboarding/IntentPrompt';
import { canRenderChatPopup } from '@/components/chat/chatPopupRoutePolicy';

// Page modules are loaded only when their route is reached. Keeping the
// provider/layout foundation static preserves auth, theme, and shell startup
// while preventing unrelated admin, media, map, and editor code from entering
// the initial browser entry chunk.
const Index = lazyRoute(() => import('@/pages/Index'));
const DisplaysOverview = lazyRoute(() => import('@/pages/DisplaysOverview'));
const DisplaysMap = lazyRoute(() => import('@/pages/DisplaysMap'));
const DisplayStateLanding = lazyRoute(() => import('@/pages/DisplayStateLanding'));
const DisplayCityLanding = lazyRoute(() => import('@/pages/DisplayCityLanding'));
const DisplayDistrictLanding = lazyRoute(() => import('@/pages/DisplayDistrictLanding'));
const DisplayDetail = lazyRoute(() => import('@/pages/DisplayDetail'));
const DisplaySignage = lazyRoute(() => import('@/pages/DisplaySignage'));
const YardSign = lazyRoute(() => import('@/pages/YardSign'));
const VoteSign = lazyRoute(() => import('@/pages/VoteSign'));
const EmbedVote = lazyRoute(() => import('@/pages/EmbedVote'));
const RelayPair = lazyRoute(() => import('@/pages/RelayPair'));
const DevelopersWelcome = lazyRoute(() => import('@/pages/developers/DevelopersWelcome'));
const DevelopersHowItWorks = lazyRoute(() => import('@/pages/developers/DevelopersHowItWorks'));
const DevelopersQuickstart = lazyRoute(() => import('@/pages/developers/DevelopersQuickstart'));
const DevelopersPairing = lazyRoute(() => import('@/pages/developers/DevelopersPairing'));
const DevelopersNowPlaying = lazyRoute(() => import('@/pages/developers/DevelopersNowPlaying'));
const DevelopersReading = lazyRoute(() => import('@/pages/developers/DevelopersReading'));
const DevelopersSongNames = lazyRoute(() => import('@/pages/developers/DevelopersSongNames'));
const DevelopersLorRelay = lazyRoute(() => import('@/pages/developers/DevelopersLorRelay'));
const DevelopersFppPlugin = lazyRoute(() => import('@/pages/developers/DevelopersFppPlugin'));
const DevelopersTroubleshooting = lazyRoute(() => import('@/pages/developers/DevelopersTroubleshooting'));
const DevelopersChangelog = lazyRoute(() => import('@/pages/developers/DevelopersChangelog'));
const DevelopersFaq = lazyRoute(() => import('@/pages/developers/DevelopersFaq'));
const SignScanRedirect = lazyRoute(() => import('@/routing/SignScanRedirect'));
const EditDisplay = lazyRoute(() => import('@/pages/EditDisplay'));
const OwnerProfile = lazyRoute(() => import('@/pages/OwnerProfile'));
const BundleDetail = lazyRoute(() => import('@/pages/BundleDetail'));
const Deals = lazyRoute(() => import('@/pages/Deals'));
const Sequences = lazyRoute(() => import('@/pages/Sequences'));
const SequencesCatalog = lazyRoute(() => import('@/pages/SequencesCatalog'));
const SequencersCatalog = lazyRoute(() => import('@/pages/SequencersCatalog'));
const SequenceSearch = lazyRoute(() => import('@/pages/SequenceSearch'));
const UploadSequencePage = lazyRoute(() => import('@/pages/UploadSequence'));
const UploadPublishedPage = lazyRoute(() => import('@/pages/UploadPublished'));
const Search = lazyRoute(() => import('@/pages/Search'));
const SequenceDetail = lazyRoute(() => import('@/pages/SequenceDetail'));
const SongDetail = lazyRoute(() => import('@/pages/SongDetail'));
const PropProductDetail = lazyRoute(() => import('@/pages/PropProductDetail'));
const PropFamilyDetail = lazyRoute(() => import('@/pages/PropFamilyDetail'));
const PropVendorDetail = lazyRoute(() => import('@/pages/PropVendorDetail'));
const Categories = lazyRoute(() => import('@/pages/Categories'));
const CategoryDetail = lazyRoute(() => import('@/pages/CategoryDetail'));
const Directory = lazyRoute(() => import('@/pages/Directory'));
const DirectoryListing = lazyRoute(() => import('@/pages/DirectoryListing'));
const DisplayTags = lazyRoute(() => import('@/pages/DisplayTags'));
const DisplayTagDetail = lazyRoute(() => import('@/pages/DisplayTagDetail'));
const UserProfile = lazyRoute(() => import('@/pages/UserProfile'));
const NotFound = lazyRoute(() => import('@/pages/NotFound'));
const Favorites = lazyRoute(() => import('@/pages/Favorites'));
const Auth = lazyRoute(() => import('@/pages/Auth'));
const ForgotPassword = lazyRoute(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazyRoute(() => import('@/pages/ResetPassword'));
const OnboardingStart = lazyRoute(() => import('@/pages/OnboardingStart'));
const ClaimProfile = lazyRoute(() => import('@/pages/ClaimProfile'));
const Onboarding = lazyRoute(() => import('@/pages/Onboarding'));
const ConfirmPlaceholderEmailChange = lazyRoute(() => import('@/pages/ConfirmPlaceholderEmailChange'));
const CustomSignUp = lazyRoute(() => import('@/pages/CustomSignUp'));
const VerifyEmail = lazyRoute(() => import('@/pages/VerifyEmail'));
const PrivacyPolicy = lazyRoute(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazyRoute(() => import('@/pages/TermsOfService'));
const DMCAPolicy = lazyRoute(() => import('@/pages/DMCAPolicy'));
const RefundPolicy = lazyRoute(() => import('@/pages/RefundPolicy'));
const SellerTerms = lazyRoute(() => import('@/pages/SellerTerms'));
const AboutUs = lazyRoute(() => import('@/pages/AboutUs'));
const Contact = lazyRoute(() => import('@/pages/Contact'));
const HelpIndex = lazyRoute(() => import('@/pages/help/HelpIndex'));
const HelpSection = lazyRoute(() => import('@/pages/help/HelpSection'));
const HelpArticle = lazyRoute(() => import('@/pages/help/HelpArticle'));
const Sell = lazyRoute(() => import('@/pages/Sell'));
const AiConnector = lazyRoute(() => import('@/pages/AiConnector'));
const RelayLanding = lazyRoute(() => import('@/pages/RelayLanding'));
const FppLanding = lazyRoute(() => import('@/pages/FppLanding'));
const RelayWhatsNew = lazyRoute(() => import('@/pages/RelayWhatsNew'));
const ForOwners = lazyRoute(() => import('@/pages/ForOwners'));
const ForOwnersFind = lazyRoute(() => import('@/pages/ForOwnersFind'));
const ForOwnersDisplayPage = lazyRoute(() => import('@/pages/ForOwnersDisplayPage'));
const ForOwnersFirstShow = lazyRoute(() => import('@/pages/ForOwnersFirstShow'));
const FitYourHouse = lazyRoute(() => import('@/pages/FitYourHouse'));
const VoiceRewards = lazyRoute(() => import('@/pages/VoiceRewards'));
const VotingVoice = lazyRoute(() => import('@/pages/VotingVoice'));
const VoiceRewardsQueue = lazyRoute(() => import('@/pages/admin/VoiceRewardsQueue'));
const EmailPreferences = lazyRoute(() => import('@/pages/EmailPreferences'));
const GenresPage = lazyRoute(() => import('@/pages/sequences/Genres'));
const FreeSequences = lazyRoute(() => import('@/pages/FreeSequences'));
const Bounties = lazyRoute(() => import('@/pages/Bounties'));
const CharitySequences = lazyRoute(() => import('@/pages/CharitySequences'));
const AdminPage = lazyRoute(() => import('@/pages/Admin'));
const TaxonomyCategories = lazyRoute(() => import('@/pages/admin/TaxonomyCategories'));
const UserCreate = lazyRoute(() => import('@/pages/admin/UserCreate'));
const UserEdit = lazyRoute(() => import('@/pages/admin/UserEdit'));
const AdminDirectoryPage = lazyRoute(() => import('@/pages/admin/AdminDirectory'));
const AdminUsersPage = lazyRoute(() => import('@/pages/admin/AdminUsersPage'));
const AdminDownloadHealthPage = lazyRoute(() => import('@/pages/admin/AdminDownloadHealthPage'));
const AdminClaimsPage = lazyRoute(() => import('@/pages/admin/AdminClaimsPage'));
const AdminDisplaysPage = lazyRoute(() => import('@/pages/admin/AdminDisplaysPage'));
const AdminSequenceEditorPage = lazyRoute(() => import('@/pages/admin/AdminSequenceEditorPage'));
const ModerationDashboardPage = lazyRoute(() => import('@/components/admin/ModerationDashboard'));
const ReportsManagementPage = lazyRoute(() => import('@/components/admin/ReportsManagement'));
const AdminMetadataReviewPage = lazyRoute(() => import('@/pages/admin/AdminMetadataReviewPage'));
const PropCatalogReview = lazyRoute(() => import('@/pages/admin/PropCatalogReview'));
const EnrichmentQueue = lazyRoute(() => import('@/pages/admin/EnrichmentQueue'));
const AdminRelays = lazyRoute(() => import('@/pages/admin/AdminRelays'));
const FeedbackReports = lazyRoute(() => import('@/pages/admin/FeedbackReports'));
const AdminFeedbackIdeas = lazyRoute(() => import('@/pages/admin/AdminFeedbackIdeas'));
const AdminDownloadLimits = lazyRoute(() => import('@/pages/admin/DownloadLimits'));
const MyDownloads = lazyRoute(() => import('@/pages/MyDownloads'));
const MySequences = lazyRoute(() => import('@/pages/MySequences'));
const MyDisplays = lazyRoute(() => import('@/pages/MyDisplays'));
const ShowTools = lazyRoute(() => import('@/pages/ShowTools'));
const EditSequence = lazyRoute(() => import('@/pages/EditSequence'));
const Videos = lazyRoute(() => import('@/pages/Videos'));
const VideoDetail = lazyRoute(() => import('@/pages/VideoDetail'));
const VideoUpload = lazyRoute(() => import('@/pages/VideoUpload'));
const PurchaseSuccess = lazyRoute(() => import('@/pages/PurchaseSuccess'));
const SellerDashboard = lazyRoute(() => import('@/pages/SellerDashboard'));
const SellerPromotions = lazyRoute(() => import('@/pages/SellerPromotions'));
const Feedback = lazyRoute(() => import('@/pages/Feedback'));
const FeedbackDetail = lazyRoute(() => import('@/pages/FeedbackDetail'));
const Roadmap = lazyRoute(() => import('@/pages/Roadmap'));
const Changelog = lazyRoute(() => import('@/pages/Changelog'));
const ActivityFeed = lazyRoute(() =>
  import('@/pages/ActivityFeed').then(({ ActivityFeed: page }) => ({ default: page })),
);
const ChatLayout = lazyRoute(() =>
  import('@/components/chat/ChatLayout').then(({ ChatLayout: layout }) => ({ default: layout })),
);
const LazyChatPopup = lazyRoute(() =>
  import('@/components/chat/ChatPopup').then(({ ChatPopup: popup }) => ({ default: popup })),
);

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[18rem] items-center justify-center p-8" role="status" aria-live="polite">
      <span className="sr-only">Loading page…</span>
      <span aria-hidden="true" className="h-2 w-24 animate-pulse rounded-full bg-muted" />
    </div>
  );
}

function DeferredChatPopup() {
  const { state, closeChat } = useChatPopup();
  const location = useLocation();
  const routeLocation = `${location.pathname}${location.search}${location.hash}`;
  const isAllowed = canRenderChatPopup(routeLocation);

  useEffect(() => {
    if (!isAllowed && state.isOpen) {
      closeChat();
    }
  }, [closeChat, isAllowed, state.isOpen]);

  return isAllowed && state.isOpen ? <LazyChatPopup /> : null;
}

function App() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);
  const [initStatus, setInitStatus] = useState<string>('Starting initialization...');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [welcomeUserEmail, setWelcomeUserEmail] = useState('');
  // Mount the lazy modal the first time it opens and keep it mounted, so the
  // dialog still plays its close animation instead of unmounting on close.
  const [welcomeModalMounted, setWelcomeModalMounted] = useState(false);
  if (showWelcomeModal && !welcomeModalMounted) setWelcomeModalMounted(true);

  // Apply theme effect throughout the app lifecycle
  useThemeEffect();

  // Drop index.html's static description once a page-specific one renders
  useDedupeStaticMeta();

  // Initialize app and check Supabase connection
  useEffect(() => {
    let isActive = true;

    const initializeApp = () => {
      let startedReadiness;

      try {
        startedReadiness = beginAppReadiness({
          config: supabaseConfig,
          onStatus: setInitStatus,
          checkDatabase: () => probeDatabase(supabase),
        });

        // Configuration is valid, so routes can mount immediately. The
        // database probe remains advisory and reports provider health later.
        setIsInitialized(true);

        void startedReadiness.databaseProbe
          .then((readiness) => {
            if (readiness.databaseError) {
              // The database probe is advisory. Keep provider failures out of
              // the global toast layer so public routes remain readable and
              // page-owned queries can present their own truthful state.
              console.warn('Database health probe unavailable; route data will report provider state locally.');
            }
          })
          .catch(() => {
            if (!isActive) return;
            console.warn('Database health probe failed unexpectedly; routes remain available.');
          });

        // Check for recent email confirmation in sessionStorage
        const emailJustConfirmed = sessionStorage.getItem('email-just-confirmed');
        const confirmedEmail = sessionStorage.getItem('confirmed-email');
        
        if (emailJustConfirmed === 'true' && confirmedEmail) {
          // Show welcome modal for users who might have refreshed
          setTimeout(() => {
            setWelcomeUserEmail(confirmedEmail);
            setShowWelcomeModal(true);
            // Clear the flags
            sessionStorage.removeItem('email-just-confirmed');
            sessionStorage.removeItem('confirmed-email');
          }, 1000);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown initialization error';
        setInitError(error instanceof Error ? error : new Error(errorMessage));
        toast.error('Failed to initialize application', {
          description: errorMessage
        });
      }
    };

    initializeApp();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    // Handle auth success/error events
    const handleShowToast = (event: CustomEvent) => {
      const { type, title, description } = event.detail;
      if (type === 'success') {
        toast.success(title, { description });
      } else if (type === 'error') {
        toast.error(title, { description });
      }
    };

    // Handle email confirmation success
    const handleEmailConfirmed = (event: CustomEvent) => {
      const { email } = event.detail;
      // Set welcome modal state
      setWelcomeUserEmail(typeof email === 'string' ? email : '');
      setShowWelcomeModal(true);
      
      // Clear any session storage flags
      sessionStorage.removeItem('email-just-confirmed');
      sessionStorage.removeItem('confirmed-email');
    };

    window.addEventListener('show-toast', handleShowToast as EventListener);
    window.addEventListener('email-confirmed', handleEmailConfirmed as EventListener);
    
    return () => {
      window.removeEventListener('show-toast', handleShowToast as EventListener);
      window.removeEventListener('email-confirmed', handleEmailConfirmed as EventListener);
    };
  }, []);

  // Network status monitoring
  useEffect(() => {
  }, []);

  if (initError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background text-foreground">
        <div className="max-w-md w-full space-y-6 text-center">
          <h1 className="text-3xl font-bold text-red-600">Initialization Error</h1>
          <div className="space-y-4">
            <p className="text-lg font-medium">{initError.message}</p>
            <div className="bg-muted p-4 rounded-lg text-sm text-left">
              <p className="font-mono">Please check:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Your .env file has VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY</li>
                <li>Your Supabase project is running</li>
                <li>Your network connection</li>
              </ul>
            </div>
            <pre className="bg-muted p-4 rounded-lg text-xs text-left overflow-auto">
              {JSON.stringify({ 
                url: import.meta.env.VITE_SUPABASE_URL ? 'Set' : 'Missing',
                key: import.meta.env.VITE_SUPABASE_ANON_KEY ? 'Set' : 'Missing'
              }, null, 2)}
            </pre>
          </div>
          <button
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            onClick={() => window.location.reload()}
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-lg font-medium">Loading your experience...</p>
          <p className="text-sm text-muted-foreground">{initStatus}</p>
        </div>
      </div>
    );
  }

  return (
    <ChatPopupProvider>
      <FeedsProvider>
      <AuthProvider>
            <RouteAnnouncer />
            <SequenceUsageResume />
            <AnalyticsIdentity />
            <SidewalkUpvoteMerger />
            <IntentPrompt suppressed={showWelcomeModal} />
            <ScrollRestoration />
            <Suspense fallback={<RouteLoadingFallback />}>
              {isStudioHost() ? <StudioRoutes /> : (
              <Routes>
            <Route
              element={
                <PublicLayout
                  pageClassName="home-page"
                  mainClassName="home-page__main"
                  mainAriaLabel="Light Hunt home"
                />
              }
            >
              <Route path="/" element={<Index />} />
            </Route>
            <Route path="/login" element={<LegacyRouteRedirect />} />
            <Route element={<FocusedLayout chrome="bare" />}>
              <Route path="/auth" element={<Auth />} />
            </Route>
            <Route element={<FocusedLayout chrome="bare" />}>
              <Route path="/auth/callback" element={<AuthCallback />} />
            </Route>
            <Route element={<FocusedLayout chrome="bare" />}>
              <Route path="/signup" element={<CustomSignUp />} />
            </Route>
            <Route element={<FocusedLayout chrome="bare" />}>
              <Route path="/verify-email" element={<VerifyEmail />} />
            </Route>
            <Route element={<FocusedLayout chrome="bare" />}>
              <Route path="/forgot-password" element={<ForgotPassword />} />
            </Route>
            <Route element={<FocusedLayout chrome="bare" />}>
              <Route path="/reset-password" element={<ResetPassword />} />
            </Route>
            <Route element={<FocusedLayout />}>
              <Route path="/onboarding/start" element={<OnboardingStart />} />
            </Route>
            <Route element={<FocusedLayout />}>
              <Route path="/claim/:token" element={<ClaimProfile />} />
            </Route>
            <Route element={<FocusedLayout />}>
              <Route path="/onboarding/email-change" element={<ConfirmPlaceholderEmailChange />} />
            </Route>
            <Route element={<AccountLayout />}>
              <Route path="/onboarding" element={<Onboarding />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="displays-overview-page"
                  mainClassName="displays-overview-page__main"
                  mainAriaLabel="Display discovery"
                />
              }
            >
              <Route path="/displays" element={<DisplaysOverview />} />
            </Route>
            <Route path="/displays/map" element={<DisplaysMap />} />
            <Route
              element={
                <PublicLayout
                  pageClassName="display-landing-page"
                  mainClassName="display-landing-page__main container mx-auto px-4 pb-8"
                  mainAriaLabel="Displays by location"
                />
              }
            >
              <Route path="/displays/in/:state" element={<DisplayStateLanding />} />
              <Route path="/displays/in/:state/:city" element={<DisplayCityLanding />} />
              <Route path="/displays/in/:state/:city/:district" element={<DisplayDistrictLanding />} />
            </Route>
            <Route path="/displays/nearby" element={<LegacyRouteRedirect />} />
            <Route path="/displays/:id" element={<LegacyRouteRedirect />} />
            <Route path="/displays-search" element={<LegacyRouteRedirect />} />
            <Route
              element={
                <PublicLayout
                  pageClassName="display-detail-page"
                  mainClassName="display-detail-page__main"
                  mainAriaLabel="Display details"
                />
              }
            >
              <Route path="/display/:id" element={<DisplayDetail />} />
            </Route>
            {/* Yard-sign print page: standalone, no PublicLayout chrome */}
            <Route path="/display/:id/signage" element={<DisplaySignage />} />
            <Route path="/display/:id/yard-sign" element={<YardSign />} />
            <Route path="/display/:id/vote-sign" element={<VoteSign />} />
            {/* Vote card for owners' own websites (public/embed.js): no site chrome */}
            <Route path="/embed/vote/:id" element={<EmbedVote />} />
            {/* Pairing gets the focused header so people can find their way back (#449). */}
            <Route element={<FocusedLayout />}>
              <Route path="/relay/pair" element={<RelayPair />} />
            </Route>
            <Route element={<PublicLayout mainAriaLabelledBy="developers-doc-title" />}>
              <Route path="/developers" element={<DevelopersWelcome />} />
              <Route path="/developers/how-it-works" element={<DevelopersHowItWorks />} />
              <Route path="/developers/quickstart" element={<DevelopersQuickstart />} />
              <Route path="/developers/pairing" element={<DevelopersPairing />} />
              <Route path="/developers/now-playing" element={<DevelopersNowPlaying />} />
              <Route path="/developers/reading" element={<DevelopersReading />} />
              <Route path="/developers/song-names" element={<DevelopersSongNames />} />
              <Route path="/developers/lor-relay" element={<DevelopersLorRelay />} />
              <Route path="/developers/fpp-plugin" element={<DevelopersFppPlugin />} />
              <Route path="/developers/troubleshooting" element={<DevelopersTroubleshooting />} />
              <Route path="/developers/changelog" element={<DevelopersChangelog />} />
              <Route path="/developers/faq" element={<DevelopersFaq />} />
            </Route>
            <Route path="/s/:id" element={<SignScanRedirect />} />
            <Route element={<FocusedLayout />}>
              <Route path="/display/edit/:id?" element={<EditDisplay />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="owner-profile-page"
                  mainClassName="owner-profile-page__main"
                  mainAriaLabel="Owner profile workspace"
                />
              }
            >
              <Route path="/owner/:id" element={<OwnerProfile />} />
            </Route>
            <Route path="/owners" element={<LegacyRouteRedirect />} />
            {/* Sequences Routes - NEW ARCHITECTURE */}
            <Route
              element={
                <PublicLayout
                  pageClassName="sequences-catalog-page"
                  mainClassName="sequences-catalog-page__main"
                  mainAriaLabel="Light Display Sequences"
                />
              }
            >
              <Route
                path="/sequences"
                element={
                  <LegacyRouteRedirect>
                    <SequencesCatalog />
                  </LegacyRouteRedirect>
                }
              />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sequence-search-page"
                  mainClassName="sequence-search-page__main"
                  mainAriaLabel="Search Light Display Sequences"
                />
              }
            >
              <Route path="/sequences/search" element={<SequenceSearch />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="genres-page"
                  mainClassName="genres-page__main"
                  mainAriaLabel="Sequence genres"
                />
              }
            >
              <Route path="/sequences/genres" element={<GenresPage />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sequences-free-page"
                  mainClassName="sequences-free-page__main"
                  mainAriaLabel="Free light display sequences"
                />
              }
            >
              <Route path="/sequences/free" element={<FreeSequences />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sequences-charity-page"
                  mainClassName="sequences-charity-page__main"
                  mainAriaLabel="Charity light display sequences"
                />
              }
            >
              <Route path="/sequences/charity" element={<CharitySequences />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="bounties-page"
                  mainClassName="bounties-page__main"
                  mainAriaLabel="Bounty Board"
                />
              }
            >
              <Route path="/bounties" element={<Bounties />} />
            </Route>
            {/* Path-based filter routes - still use Sequences component */}
            <Route
              element={
                <PublicLayout
                  pageClassName="sequences-filter-page"
                  mainClassName="sequences-filter-page__main"
                  mainAriaLabel="Light Display Sequences"
                />
              }
            >
              <Route path="/sequences/:software" element={<Sequences />} />
              <Route path="/sequences/:software/:holiday" element={<Sequences />} />
              <Route path="/sequences/holiday/:holiday" element={<Sequences />} />
              <Route path="/sequences/genre/:genre" element={<Sequences />} />
              <Route path="/sequences/artist/:artist" element={<Sequences />} />
            </Route>
            <Route element={<FocusedLayout />}>
              <Route path="/upload-sequence" element={<UploadSequencePage />} />
            </Route>
            <Route element={<FocusedLayout chrome="bare" />}>
              <Route path="/upload-sequence/published/:sequenceId" element={<UploadPublishedPage />} />
            </Route>
            {/* Creators directory routes. /sequencers is the canonical URL
                (per docs/ux-redesign/00-route-matrix.md); /creators is an
                additive alias rendering the same catalog. */}
            <Route
              element={
                <PublicLayout
                  pageClassName="sequencers-page"
                  mainClassName="sequencers-page__main bg-background"
                  mainAriaLabel="Light display creators"
                />
              }
            >
              <Route path="/sequencers" element={<SequencersCatalog />} />
              <Route path="/creators" element={<SequencersCatalog />} />
            </Route>
            {/* Redirect old sequencer profile URLs through the canonical alias resolver. */}
            <Route path="/sequencer/:username" element={<LegacyRouteRedirect />} />
            <Route element={<FocusedLayout />}>
              <Route path="/search" element={<Search />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sequence-detail-page"
                  mainClassName="sequence-detail-page__main bg-background"
                  mainAriaLabel="Sequence details"
                />
              }
            >
              <Route path="/sequence/:id" element={<SequenceDetail />} />
              <Route path="/bundle/:slug" element={<BundleDetail />} />
              <Route path="/deals" element={<Deals />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="song-detail-page"
                  mainClassName="song-detail-page__main bg-background"
                  mainAriaLabel="Song detail workspace"
                />
              }
            >
              <Route path="/songs/:slug" element={<SongDetail />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="prop-catalog-page"
                  mainClassName="prop-catalog-page__main bg-background"
                  mainAriaLabel="Prop catalog"
                />
              }
            >
              <Route path="/props/:productSlug" element={<PropProductDetail />} />
              <Route path="/prop-types/:familySlug" element={<PropFamilyDetail />} />
              <Route path="/prop-makers/:vendorSlug" element={<PropVendorDetail />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="categories-page"
                  mainClassName="categories-page__main bg-gray-900 text-white"
                  mainAriaLabel="Categories"
                />
              }
            >
              <Route path="/categories" element={<Categories />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="category-detail-page"
                  mainClassName="category-detail-page__main bg-background"
                  mainAriaLabel="Category detail"
                />
              }
            >
              <Route path="/categories/:slug" element={<CategoryDetail />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="directory-page"
                  mainClassName="directory-page__main container mx-auto px-4 pb-8"
                  mainAriaLabel="Sequence directory"
                />
              }
            >
              <Route path="/directory" element={<Directory />} />
              <Route path="/directory/:slug" element={<DirectoryListing />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="display-tags-page"
                  mainClassName="display-tags-page__main bg-background"
                  mainAriaLabel="Display tags"
                />
              }
            >
              <Route path="/tags" element={<DisplayTags />} />
              <Route path="/tags/:slug" element={<DisplayTagDetail />} />
            </Route>
            <Route element={<FocusedLayout />}>
              <Route path="/purchase-success/:sequenceId" element={<PurchaseSuccess />} />
            </Route>
            <Route path="/marketplace" element={<LegacyRouteRedirect />} />
            <Route path="/library" element={<LegacyRouteRedirect />} />
            <Route path="/account/library" element={<LegacyRouteRedirect />} />
            <Route path="/account/purchases" element={<LegacyRouteRedirect />} />
            <Route element={<AccountLayout />}>
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/seller-dashboard" element={<SellerDashboard />} />
              <Route path="/seller/promotions" element={<SellerPromotions />} />
            </Route>
            <Route element={<AccountLayout />}>
              <Route path="/my-downloads" element={<MyDownloads />} />
              <Route path="/favorites" element={<Favorites />} />
            </Route>
            <Route element={<AccountLayout />}>
              <Route path="/my-sequences" element={<MySequences />} />
            </Route>
            <Route element={<AccountLayout />}>
              <Route path="/my-displays" element={<MyDisplays />} />
              <Route path="/for-owners/show" element={<ShowTools />} />
            </Route>
            {/* Lighting Studio moved to studio.lightshunt.com (StudioRoutes).
                Old app paths and invitation/presentation links already sent
                to people hop across to the studio host. */}
            <Route path="/studio" element={<CrossHostRedirect to="studio" />} />
            <Route path="/studio/settings" element={<CrossHostRedirect to="studio" />} />
            <Route path="/studio/projects/:projectId" element={<CrossHostRedirect to="studio" />} />
            <Route path="/invitations/:token" element={<CrossHostRedirect to="studio" />} />
            <Route path="/p/:publicToken" element={<CrossHostRedirect to="studio" />} />
            <Route
              element={
                <PublicLayout
                  pageClassName="profile-public-page"
                  mainClassName="profile-public-main container mx-auto px-4 pb-8"
                  mainAriaLabel="Public profile"
                />
              }
            >
              <Route path="/profile/:username" element={<UserProfile />} />
            </Route>
            <Route element={<FocusedLayout />}>
              <Route path="/my-sequences/edit/:id" element={<EditSequence />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="videos-page"
                  mainClassName="videos-page__main container mx-auto px-4 pb-8 max-w-6xl"
                  mainAriaLabel="Video library"
                />
              }
            >
              <Route path="/videos" element={<Videos />} />
              <Route path="/videos/:id" element={<VideoDetail />} />
            </Route>
            <Route element={<FocusedLayout />}>
              <Route path="/videos/upload" element={<VideoUpload />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="feedback-page"
                  mainClassName="feedback-page__main container mx-auto px-4 pb-8 max-w-6xl"
                  mainAriaLabel="Community feedback"
                />
              }
            >
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/feedback/:slug" element={<FeedbackDetail />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="roadmap-page"
                  mainClassName="roadmap-page__main container mx-auto px-4 pb-8"
                  mainAriaLabel="Product roadmap"
                />
              }
            >
              <Route path="/roadmap" element={<Roadmap />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="changelog-page"
                  mainClassName="changelog-page__main container mx-auto px-4 pb-8"
                  mainAriaLabel="Changelog"
                />
              }
            >
              <Route path="/changelog" element={<Changelog />} />
            </Route>
            <Route path="/realtime" element={<LegacyRouteRedirect />} />
            <Route element={<AccountLayout fullBleed />}>
              <Route path="/messages" element={<ChatLayout embedded />} />
            </Route>
            <Route element={<AccountLayout />}>
              <Route path="/activity-feed" element={<ActivityFeed />} />
            </Route>
            <Route element={<AdminProtection><AdminLayout variant="taxonomy" /></AdminProtection>}>
              <Route path="/admin/taxonomy/categories" element={<TaxonomyCategories />} />
            </Route>
            <Route element={<AdminProtection><AdminLayout variant="user-form" /></AdminProtection>}>
              <Route path="/admin/users/new" element={<UserCreate />} />
              <Route path="/admin/users/edit/:userId" element={<UserEdit />} />
            </Route>
            <Route element={<AdminProtection><AdminLayout variant="operations" /></AdminProtection>}>
              <Route path="/admin" element={<AdminPage />} />
            </Route>
            <Route element={<AdminProtection><AdminLayout /></AdminProtection>}>
              <Route path="/admin/sequences" element={<AdminSequenceEditorPage />} />
              <Route path="/admin/reports" element={<ReportsManagementPage />} />
              <Route path="/admin/moderation" element={<ModerationDashboardPage />} />
              <Route path="/admin/metadata-review" element={<AdminMetadataReviewPage />} />
              <Route path="/admin/claims" element={<AdminClaimsPage />} />
              <Route path="/admin/displays" element={<AdminDisplaysPage />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/download-health" element={<AdminDownloadHealthPage />} />
              <Route path="/admin/directory" element={<AdminDirectoryPage />} />
              <Route path="/admin/prop-catalog" element={<PropCatalogReview />} />
              <Route path="/admin/enrichment" element={<EnrichmentQueue />} />
              <Route path="/admin/relays" element={<AdminRelays />} />
              <Route path="/admin/feedback-reports" element={<FeedbackReports />} />
              <Route path="/admin/feedback-ideas" element={<AdminFeedbackIdeas />} />
              <Route path="/admin/voice-rewards" element={<VoiceRewardsQueue />} />
              <Route path="/admin/download-limits" element={<AdminDownloadLimits />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="privacy-page legal-reading-page"
                  mainClassName="privacy-page__main legal-reading-page__main container mx-auto px-4 pb-8 max-w-6xl"
                  mainAriaLabelledBy="privacy-page-heading"
                />
              }
            >
              <Route path="/privacy" element={<PrivacyPolicy />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="terms-page legal-reading-page"
                  mainClassName="terms-page__main legal-reading-page__main container mx-auto px-4 pb-8 max-w-6xl"
                  mainAriaLabelledBy="terms-page-heading"
                />
              }
            >
              <Route path="/terms" element={<TermsOfService />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="dmca-page legal-reading-page"
                  mainClassName="dmca-page__main legal-reading-page__main container mx-auto px-4 pb-8 max-w-6xl"
                  mainAriaLabelledBy="dmca-page-heading"
                />
              }
            >
              <Route path="/dmca" element={<DMCAPolicy />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="refund-page legal-reading-page"
                  mainClassName="refund-page__main legal-reading-page__main container mx-auto px-4 pb-8 max-w-6xl"
                  mainAriaLabelledBy="refund-page-heading"
                />
              }
            >
              <Route path="/refunds" element={<RefundPolicy />} />
            </Route>
            <Route path="/cookies" element={<LegacyRouteRedirect />} />
            <Route
              element={
                <PublicLayout
                  pageClassName="seller-terms-page"
                  mainClassName="seller-terms-page__main container mx-auto px-4 pb-8 max-w-6xl"
                  mainAriaLabelledBy="seller-terms-title"
                />
              }
            >
              <Route path="/seller-terms" element={<SellerTerms />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="about-page"
                  mainClassName="about-page__main container mx-auto px-4 pb-8 max-w-4xl"
                  mainAriaLabelledBy="about-title"
                />
              }
            >
              <Route path="/about" element={<AboutUs />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="help-page"
                  mainClassName="help-page__main container mx-auto px-4 pb-8 max-w-4xl"
                  mainAriaLabelledBy="help-title"
                />
              }
            >
              <Route path="/help" element={<HelpIndex />} />
              <Route path="/help/:section" element={<HelpSection />} />
              <Route path="/help/:section/:slug" element={<HelpArticle />} />
            </Route>
            <Route path="/faq" element={<LegacyRouteRedirect />} />
            <Route path="/getting-started" element={<LegacyRouteRedirect />} />
            <Route path="/display-guide" element={<LegacyRouteRedirect />} />
            <Route path="/sequence-guide" element={<LegacyRouteRedirect />} />
            <Route
              element={
                <PublicLayout
                  pageClassName="contact-page"
                  mainClassName="contact-page__main container mx-auto px-4 pb-8 max-w-4xl"
                  mainAriaLabel="Contact"
                />
              }
            >
              <Route path="/contact" element={<Contact />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sell-page"
                  mainClassName="voices-main"
                  mainAriaLabelledBy="voices-title"
                />
              }
            >
              <Route path="/voices" element={<VoiceRewards />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sell-page"
                  mainClassName="voices-main"
                  mainAriaLabelledBy="voting-title"
                />
              }
            >
              <Route path="/voices/voting" element={<VotingVoice />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sell-page"
                  mainClassName="sell-main"
                  mainAriaLabelledBy="relay-title"
                />
              }
            >
              <Route path="/relay" element={<RelayLanding />} />
              <Route path="/relay/whats-new" element={<RelayWhatsNew />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sell-page"
                  mainClassName="sell-main"
                  mainAriaLabelledBy="fpp-title"
                />
              }
            >
              <Route path="/fpp" element={<FppLanding />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sell-page"
                  mainClassName="sell-main"
                  mainAriaLabelledBy="for-owners-title"
                />
              }
            >
              <Route path="/for-owners" element={<ForOwners />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sell-page"
                  mainClassName="sell-main"
                  mainAriaLabelledBy="for-owners-find-title"
                />
              }
            >
              <Route path="/for-owners/find" element={<ForOwnersFind />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sell-page"
                  mainClassName="sell-main"
                  mainAriaLabelledBy="for-owners-display-page-title"
                />
              }
            >
              <Route path="/for-owners/display-page" element={<ForOwnersDisplayPage />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sell-page"
                  mainClassName="sell-main"
                  mainAriaLabelledBy="for-owners-first-show-title"
                />
              }
            >
              <Route path="/for-owners/first-show" element={<ForOwnersFirstShow />} />
            </Route>
            <Route element={<PublicLayout pageClassName="sell-page" mainClassName="sell-main" mainAriaLabelledBy="fit-title" />}>
              <Route path="/fit" element={<FitYourHouse />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sell-page"
                  mainClassName="sell-main"
                  mainAriaLabelledBy="sell-title"
                />
              }
            >
              <Route path="/sell" element={<Sell />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="email-preferences-page"
                  mainAriaLabelledBy="email-preferences-title"
                />
              }
            >
              <Route path="/email-preferences" element={<EmailPreferences />} />
            </Route>
            <Route
              element={
                <PublicLayout
                  pageClassName="sell-page"
                  mainClassName="sell-main"
                  mainAriaLabelledBy="ai-title"
                />
              }
            >
              <Route path="/ai" element={<AiConnector />} />
            </Route>
            {/* The installer marketing page is the studio host's home page. */}
            <Route path="/installers" element={<CrossHostRedirect to="studio" />} />
            <Route path="/map" element={<LegacyRouteRedirect />} />
            <Route
              element={
                <PublicLayout
                  pageClassName="not-found-page"
                  mainClassName="not-found-page__main"
                  mainAriaLabelledBy="not-found-title"
                />
              }
            >
              <Route path="*" element={<NotFound />} />
            </Route>
              </Routes>
              )}
            </Suspense>
          
          {/* Welcome Modal for newly confirmed users */}
          {welcomeModalMounted && (
            <Suspense fallback={null}>
              <WelcomeModal
                isOpen={showWelcomeModal}
                userEmail={welcomeUserEmail}
                onClose={() => setShowWelcomeModal(false)}
              />
            </Suspense>
          )}

          <DeferredChatPopup />

          <Toaster
            position="top-center"
            richColors
            closeButton
            expand
            duration={6000}
            toastOptions={{
              style: {
                fontSize: '16px',
                padding: '16px 20px',
                minHeight: '60px',
                borderRadius: '12px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              },
              className: 'font-medium',
            }}
          />
      </AuthProvider>
      </FeedsProvider>
    </ChatPopupProvider>
  );
}

export default App;
